import {
    ChangeDetectionStrategy,
    Component,
    input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
    signal,
    effect,
} from '@angular/core';
import {
    IOnboardingCompletionData,
    IOnboardingUserCompletion,
    IOnboardingUserSettingsSaveModel,
} from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { IItemsListAction2 } from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiOnboardingCompletionActionsBuilderService } from '../fi-onboarding-completion-actions-builder.service';
import { Subject } from 'rxjs';
import { AppTextService } from '@fi-shared/app-text';
import { FiOnboardingCompletionHelperService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-helper.service';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-onboarding-completion-container',
    templateUrl: './fi-onboarding-completion-container.component.html',
    styleUrl: './fi-onboarding-completion-container.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-completion-container' },
    standalone: false,
})
export class FiOnboardingCompletionContainerComponent implements OnInit, OnDestroy {
    public onboardingCompletionData = input.required<IOnboardingCompletionData>();

    private readonly fiOnboardingCompletionHelperService = inject(FiOnboardingCompletionHelperService);
    private readonly fiOnboardingCompletionActionsBuilderService = inject(FiOnboardingCompletionActionsBuilderService);
    private readonly fiAppText = inject(AppTextService);

    protected currentUserCompletion = signal<IOnboardingUserCompletion | undefined>(undefined);

    protected stepsToCompleteForEachUserText = computed(() => {
        return this.fiAppText.get('onboarding.steps_to_complete_for_each_user', {
            '{0}': this.fiOnboardingCompletionHelperService.getOnboardingStepsCount(
                this.onboardingCompletionData().Onboarding,
            ),
        });
    });

    protected actions: IItemsListAction2<IOnboardingUserCompletion>[] = [];

    private readonly onboardingDataEffect = effect(() => {
        const onboardingCompletionData = this.onboardingCompletionData();
        const isSynchronized = onboardingCompletionData.IsSynchronized;
        if (!isSynchronized) {
            this.currentUserCompletion.set(onboardingCompletionData.UserCompletion);
        } else {
            this.currentUserCompletion.set(undefined);
        }

        this.actions =
            this.fiOnboardingCompletionActionsBuilderService.getMyOnboardingCompletionActions(/*{
            onboardingId: onboardingCompletionData.Onboarding.Id,
        }*/);
    });

    public ngOnInit(): void {
        this.subscribeOnUserSettingsChanged();
    }

    private subscribeOnUserSettingsChanged(): void {
        this.fiOnboardingCompletionActionsBuilderService.subscribeOnUserSettingsChanged(
            this.ngOnDestroy$,
            (newUserSettings) => this.updateUserCompletionWithNewSettings(newUserSettings),
        );
    }

    private updateUserCompletionWithNewSettings(newUserSettings: IOnboardingUserSettingsSaveModel): void {
        this.currentUserCompletion.update((current) => {
            return {
                ...current,
                IsAtSideWidgets: isDefined(newUserSettings.IsAtSideWidgets)
                    ? newUserSettings.IsAtSideWidgets
                    : current?.IsAtSideWidgets,
                IsHiddenFromHomePage: isDefined(newUserSettings.IsHiddenFromHomePage)
                    ? newUserSettings.IsHiddenFromHomePage
                    : current?.IsHiddenFromHomePage,
            };
        });
    }

    protected onBackButtonClick(): void {
        if (this.onboardingCompletionData().IsSynchronized && isDefined(this.currentUserCompletion())) {
            this.currentUserCompletion.set(undefined);
        } else {
            window.history.back();
        }
    }

    protected onCurrentUserCompletionChanged($event: IOnboardingUserCompletion) {
        this.currentUserCompletion.set($event);
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
